// expose-services.js — Brief 156, widened by Brief 193.
//
// Exposes a minimal `window.services` surface on dev / test deploys so Brief
// 155's seed snippets (tools/seed/<state>.js) can reach the real learner-store
// funnel (`markItemCompleted`) from the DevTools Console at
// test.kungfuenglish.com. On `npm run dev` the snippet falls back to importing
// raw `/src/**` modules; the esbuild-bundled deploy does not serve `/src/**`,
// so without this hook the snippet 404s its dynamic imports and makes no writes.
//
// Brief 193 adds the second consumer: the synthetic-learner harness
// (`tests/synthetic-learner/`) must run its full-pathway walk against the
// DEPLOYED test server, not just the dev server, so Chris's China-side run
// measures the real server + CDN path. Its oracle read app state the same way
// the seed snippets did — `import("/src/platform-services.js")` — which the
// bundled deploy cannot serve. The oracle now prefers this bridge and falls
// back to the raw-source import, so one walk covers both targets.
//
// SECURITY INVARIANT — production NEVER exposes services. The gate is an
// EXPLICIT HOSTNAME ALLOWLIST (not a build flag). The same bundle deployed to
// test.kungfuenglish.com exposes services; deployed to the production apex
// (kungfuenglish.com), a `www.` alias, or any CDN host (e.g.
// video.kungfuenglish.com) it does not — those hostnames are simply not in the
// allowlist. Hostname-gating beats build-flag gating here because it needs no
// separate dev/test build artifact: one bundle, behaviour decided at runtime by
// where it is served. **The surface grows; the host list does not** (Brief 193
// §gotchas).
//
// EXPOSED SURFACE — kept minimal and grown one surface at a time. A blanket
// `window.services = services` would hand a DevTools-savvy learner a larger
// blast radius (audio, pathway, content, …), so each entry below names its
// consumer:
//   • `learner`    — Brief 155 seed snippets (`markItemCompleted`) + the Brief
//                    193 oracle's per-item `getItemProgress` /
//                    `getCompletedItemIds` / `getSetting` reads.
//   • `itemMedia`  — Brief 193 words-met check (criterion 5). The met-WORD pool
//                    is DERIVED, not stored: `getCompletedItemIds()` ×
//                    `getWordsForItem()` is exactly what `sr-engine.js`
//                    computes, so the harness cannot verify it without this.
//   • `getActiveEngineSnapshot()` — Brief 193 READ-ONLY accessor for the
//                    currently-mounted exercise engine's `snapshot()`. Returns a
//                    fresh plain snapshot object plus the mounted component's
//                    tag name (criterion 3's declared-type-vs-mounted-component
//                    check); it exposes no engine methods, so nothing here can
//                    drive or mutate an engine.
//
// If anyone ever proposes adding a production hostname to the allowlist, they
// MUST justify it in their PR and add a unit test that explicitly fails on the
// production hostname being allowed.

/**
 * Hostnames permitted to expose `window.services`. Production hostnames
 * (`kungfuenglish.com` + `www.`/CDN aliases) are absent by design — this is an
 * allowlist, so anything not listed (and not `*.local`) is denied.
 */
export const SERVICES_EXPOSE_HOSTS = new Set([
  "test.kungfuenglish.com",
  "localhost",
  "127.0.0.1",
]);

/**
 * The exposed surface's key names, in one place so the production-exclusion
 * unit test can assert EVERY one of them is absent on a production hostname
 * without the test drifting from the implementation (Brief 193 criterion 14).
 */
export const EXPOSED_SERVICE_KEYS = ["learner", "itemMedia", "getActiveEngineSnapshot"];

/**
 * Collect every element in a document, piercing OPEN shadow roots. Mirrors the
 * harness oracle's `deepEls`; kept here (rather than imported) because this
 * module must stay dependency-free — it is loaded on every page view.
 * @param {any} root
 * @returns {any[]}
 */
function deepElements(root) {
  /** @type {any[]} */ const out = [];
  /** @param {any} n */
  const visit = (n) => {
    if (!n) return;
    out.push(n);
    const sr = n.shadowRoot;
    if (sr && sr.children) for (const c of sr.children) visit(c);
    if (n.children) for (const c of n.children) visit(c);
  };
  visit(root);
  return out;
}

/**
 * READ-ONLY snapshot of whichever exercise engine is mounted right now.
 *
 * Finds the first element under the document that carries an `engine` with a
 * `snapshot()` method — in practice the exercise component the
 * `<kfe-exercise-host>` router mounted (`kfe-l2b-exercise`,
 * `kfe-haai-exercise`, …). Media/HTML screens have no engine and return a
 * record with `snapshot: null`, which is the correct answer for them.
 *
 * Returns a NEW plain object every call; the engine instance itself is never
 * handed out, so this cannot be used to drive an exercise.
 *
 * @param {any} doc  a Document (injected for tests)
 * @returns {{ tag: string, snapshot: any } | null}
 */
export function activeEngineSnapshot(doc) {
  const root = doc && doc.documentElement;
  if (!root) return null;
  const els = deepElements(root);
  const host = els.find((e) => String(e.tagName ?? "").toLowerCase() === "kfe-exercise-host");
  const scope = host ? deepElements(host) : els;
  const mounted = scope.find(
    (e) =>
      String(e.tagName ?? "").toLowerCase().startsWith("kfe-") &&
      e.engine &&
      typeof e.engine.snapshot === "function",
  );
  if (mounted) {
    let snapshot = null;
    try {
      snapshot = mounted.engine.snapshot();
    } catch {
      /* a half-initialised engine can throw; report the tag with a null snapshot */
    }
    return { tag: String(mounted.tagName).toLowerCase(), snapshot };
  }
  // No engine-bearing component. Still report WHICH exercise component mounted
  // (criterion 3's mounted-component check applies to media/HTML items too).
  const anyExercise = scope.find((e) => {
    const t = String(e.tagName ?? "").toLowerCase();
    return t.startsWith("kfe-") && t !== "kfe-exercise-host" && /exercise|record|music|meter/.test(t);
  });
  return anyExercise ? { tag: String(anyExercise.tagName).toLowerCase(), snapshot: null } : null;
}

/**
 * Expose the allowlisted `window.services` surface when the current hostname is
 * allowlisted (or a `*.local` Bonjour / mDNS dev host). Pure and
 * dependency-injected so the gate is unit-testable without a real DOM — same
 * shape as `registerServiceWorker` (Brief 111).
 *
 * @param {object} deps
 * @param {{ learner: unknown, itemMedia?: unknown }} deps.services  the live platform services
 * @param {{ hostname: string }} deps.location  the page location (inject for tests)
 * @param {Record<string, any>} [deps.win]      the global object to assign onto
 * @param {any} [deps.doc]                      the document the engine accessor reads (defaults to win.document)
 * @returns {boolean} whether services were exposed
 */
export function exposeServicesIfAllowed({ services, location, win, doc }) {
  const host = (location && location.hostname) || "";
  const allowed = SERVICES_EXPOSE_HOSTS.has(host) || host.endsWith(".local");
  if (allowed && win) {
    const document_ = doc ?? win.document;
    win.services = {
      learner: services.learner,
      itemMedia: services.itemMedia,
      getActiveEngineSnapshot: () => activeEngineSnapshot(document_),
    };
  }
  return allowed;
}
